import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";
import { getWeightChartData } from "../../../../../services/clientDetails";

// ─── Types ────────────────────────────────────────────────────────────────────
interface WeightEntry {
  date: string;
  weight: number;
}

interface WeightChartProps {
  userId: number;
  targetWeight?: number;
}

interface DotProps {
  cx?: number;
  cy?: number;
  index?: number;
  dataLength?: number;
  [key: string]: unknown;
}

interface ApiPoint {
  date: string;
  weight: number | null;
  weight_unit: string | null;
}

interface WeightApiResponse {
  user_id: number;
  client_since: string;
  current_weight: number | null;
  target_weight: number | null;
  points: ApiPoint[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatApiDate(isoDate: string): string {
  const [, mm, dd] = isoDate.split("-");
  const day = parseInt(dd, 10);
  const month = MONTHS[parseInt(mm, 10) - 1];
  const yearShort = isoDate.slice(2, 4);
  return `${day} ${month}, ${yearShort}`;
}

const CHART_MARGIN = { top: 20, right: 24, left: 24, bottom: 0 };
const X_PAD        = 10;
const MIN_VISIBLE  = 4;

// ─── Component ────────────────────────────────────────────────────────────────
const WeightChart: React.FC<WeightChartProps> = ({ userId, targetWeight }) => {
  const [fullData, setFullData] = useState<WeightEntry[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [currentWeight, setCurrentWeight] = useState<number | undefined>(undefined);
  const [target,   setTarget]   = useState<number | undefined>(targetWeight);

  const [viewStart,  setViewStart]  = useState(0);
  const [viewEnd,    setViewEnd]    = useState(0);
  const [isZooming,  setIsZooming]  = useState(false);

  const [isPanning,    setIsPanning]    = useState(false);
  const [panOriginX,   setPanOriginX]   = useState(0);
  const [panSnapStart, setPanSnapStart] = useState(0);
  const [panSnapEnd,   setPanSnapEnd]   = useState(0);

  const wrapperRef    = useRef<HTMLDivElement>(null);
  const lastMousePos  = useRef<{ x: number; y: number } | null>(null);

  // fractional current position (for lerp) and desired target
  const viewStartF  = useRef(0);
  const viewEndF    = useRef(0);
  const targetStart = useRef(0);
  const targetEnd   = useRef(0);
  const rafId       = useRef<number | null>(null);

  // ── Data fetch ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data: WeightApiResponse = await getWeightChartData(String(userId));

        const points: WeightEntry[] = (data.points ?? [])
          .filter((p) => p.weight !== null)
          .map((p) => ({
            date: formatApiDate(p.date),
            weight: p.weight as number,
          }));

        setFullData(points);
        setCurrentWeight(data.current_weight ?? undefined);
        setTarget(data.target_weight ?? targetWeight);
      } catch (err) {
        console.error("WeightChart fetch error:", err);
        setFullData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Initialise full view once data arrives
  useEffect(() => {
    if (fullData.length > 0) {
      const last = fullData.length - 1;
      viewStartF.current  = 0;
      viewEndF.current    = last;
      targetStart.current = 0;
      targetEnd.current   = last;
      setViewStart(0);
      setViewEnd(last);
    }
  }, [fullData.length]);

  // Cancel any in-flight RAF on unmount
  useEffect(() => () => { if (rafId.current) cancelAnimationFrame(rafId.current); }, []);

  // Prevent page scroll while the cursor is over the chart
  // (zoom logic itself is in the React onWheel handler below)
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.preventDefault();
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, [loading]); // re-attach after shimmer → chart swap

  // ── Smooth zoom animation (lerp each rAF frame toward target) ───────────────
  const animateTick = useCallback(() => {
    const s  = viewStartF.current;
    const e  = viewEndF.current;
    const ts = targetStart.current;
    const te = targetEnd.current;
    const newS = s + (ts - s) * 0.3;
    const newE = e + (te - e) * 0.3;
    viewStartF.current = newS;
    viewEndF.current   = newE;
    setViewStart(Math.round(newS));
    setViewEnd(Math.round(newE));
    if (Math.abs(newS - ts) > 0.1 || Math.abs(newE - te) > 0.1) {
      rafId.current = requestAnimationFrame(animateTick);
    } else {
      viewStartF.current = ts;
      viewEndF.current   = te;
      setViewStart(ts);
      setViewEnd(te);
      rafId.current = null;
      setIsZooming(false);
    }
  }, []); // only reads/writes refs and stable setters

  const zoomTo = useCallback((newStart: number, newEnd: number) => {
    targetStart.current = newStart;
    targetEnd.current   = newEnd;
    setIsZooming(true);
    if (!rafId.current) {
      rafId.current = requestAnimationFrame(animateTick);
    }
  }, [animateTick]);

  // ── Scroll-wheel zoom ────────────────────────────────────────────────────────
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const total = fullData.length;
    if (!total) return;

    const el = wrapperRef.current;
    if (!el) return;

    // Use the *target* window (not animated display) so rapid scrolls stack correctly
    const tStart = targetStart.current;
    const tEnd   = targetEnd.current;
    const size   = tEnd - tStart + 1;

    const rect  = el.getBoundingClientRect();
    const plotW = el.offsetWidth - CHART_MARGIN.left - CHART_MARGIN.right - X_PAD * 2;
    const cursorRatio = Math.max(0, Math.min(1,
      (e.clientX - rect.left - CHART_MARGIN.left - X_PAD) / plotW
    ));
    const anchorIdx = Math.round(tStart + cursorRatio * (size - 1));

    const factor  = e.deltaY < 0 ? 0.88 : 1 / 0.88;
    const newSize = Math.max(MIN_VISIBLE, Math.min(total, Math.round(size * factor)));

    let newStart = Math.round(anchorIdx - cursorRatio * (newSize - 1));
    newStart = Math.max(0, Math.min(total - newSize, newStart));

    zoomTo(newStart, newStart + newSize - 1);
  };

  // Re-dispatch a mousemove after zoom settles so recharts shows the right tooltip.
  useEffect(() => {
    if (isZooming || !lastMousePos.current) return;
    const svg = wrapperRef.current?.querySelector("svg");
    if (!svg) return;
    const { x, y } = lastMousePos.current;
    svg.dispatchEvent(
      new MouseEvent("mousemove", { bubbles: true, cancelable: true, clientX: x, clientY: y })
    );
  }, [isZooming, viewStart, viewEnd]);

  // ── Drag-to-pan ──────────────────────────────────────────────────────────────
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    setPanOriginX(e.clientX);
    setPanSnapStart(viewStart);
    setPanSnapEnd(viewEnd);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Always track position so the post-zoom effect can re-fire tooltip
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    if (!isPanning) return;
    const el = wrapperRef.current;
    if (!el) return;

    const size       = panSnapEnd - panSnapStart + 1;
    const plotW      = el.offsetWidth - CHART_MARGIN.left - CHART_MARGIN.right - X_PAD * 2;
    const pxPerPoint = plotW / Math.max(1, size - 1);
    const shift      = Math.round(-(e.clientX - panOriginX) / pxPerPoint);
    const newStart   = Math.max(0, Math.min(fullData.length - size, panSnapStart + shift));

    if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }
    viewStartF.current  = newStart;
    viewEndF.current    = newStart + size - 1;
    targetStart.current = newStart;
    targetEnd.current   = newStart + size - 1;
    setViewStart(newStart);
    setViewEnd(newStart + size - 1);
  };

  const handleMouseUp = () => setIsPanning(false);
  const handleMouseLeave = () => {
    setIsPanning(false);
    lastMousePos.current = null;
  };

  const resetZoom = () => zoomTo(0, fullData.length - 1);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const visibleData      = fullData.slice(viewStart, viewEnd + 1);
  const visibleCount     = visibleData.length;
  const isZoomed         = fullData.length > 0 && visibleCount < fullData.length;
  const tickInterval  = Math.max(0, Math.ceil(visibleCount / 6) - 1);
  const isDotTick     = (index: number) => index % (tickInterval + 1) === 0;

  // ─── Shimmer ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md p-5 mx-6 mb-4">
        <div className="flex justify-between mb-4">
          <div className="h-4 w-28 bg-gray-100 rounded shimmer" />
          <div className="h-4 w-36 bg-gray-100 rounded shimmer" />
        </div>
        <div className="h-[120px] bg-gray-100 rounded shimmer" />
        <style>{`
          @keyframes shimmer {
            0%   { background-position: -1000px 0; }
            100% { background-position:  1000px 0; }
          }
          .shimmer {
            animation: shimmer 2s infinite;
            background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
            background-size: 1000px 100%;
          }
        `}</style>
      </div>
    );
  }

  if (fullData.length === 0) {
    return (
      <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md px-5 pt-3 pb-3 mx-6 mb-4">
        <span className="text-[13px] font-semibold font-inter text-[#1F1F1F] tracking-wide uppercase">
          Weight <span className="font-normal normal-case">(kg)</span>
        </span>
        <div className="flex items-center justify-center h-[100px] text-[13px] font-inter text-[#8E8E93]">
          No weight data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md px-5 pt-3 pb-3 mx-6 mb-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[13px] 3xl:text-[14px] font-semibold font-inter text-[#1F1F1F] tracking-wide uppercase">
          Weight <span className="font-normal normal-case">(kg)</span>
        </span>

        <div className="flex items-center gap-3">
          {isZoomed && (
            <button
              onClick={resetZoom}
              className="text-[10px] font-inter font-medium text-[#0DB5B5] bg-[#E6F9F9] hover:bg-[#d0f4f4] px-2 py-0.5 rounded transition-colors"
            >
              Reset zoom
            </button>
          )}
          {currentWeight !== undefined && (
            <span className="text-[12px] 3xl:text-[13px] font-inter text-[#646464]">
              Current weight :{" "}
              <span className="font-semibold text-[#007E7F]">{currentWeight}kg</span>
            </span>
          )}
          {currentWeight !== undefined && target !== undefined && (
            <span className="text-[#D1D5DB] select-none">|</span>
          )}
          {target !== undefined && (
            <span className="text-[12px] 3xl:text-[13px] font-inter text-[#646464]">
              Target weight :{" "}
              <span className="font-semibold text-[#1F1F1F]">{target}kg</span>
            </span>
          )}
        </div>
      </div>

      {/* ── Chart ── */}
      <div
        ref={wrapperRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onDoubleClick={resetZoom}
        style={{
          userSelect: "none",
          cursor: isPanning ? "grabbing" : "grab",
        }}
      >
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={visibleData} margin={CHART_MARGIN}>
            <YAxis hide domain={["auto", "auto"]} tickCount={2} width={0} />

            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />

            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8E8E93", fontWeight: 400 }}
              dy={8}
              padding={{ left: X_PAD, right: X_PAD }}
              interval={tickInterval}
            />

            <Tooltip
              contentStyle={{
                background: "#fff",
                border: "0.5px solid #E5E7EB",
                borderRadius: "6px",
                fontSize: 12,
                fontFamily: "Inter, sans-serif",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
              formatter={(value: string | number | undefined) =>
                [`${value ?? ""} kg`, "Weight"] as [string, string]
              }
              labelStyle={{ color: "#646464", marginBottom: 2 }}
            />

            <Line
              type="monotone"
              dataKey="weight"
              stroke="#0DB5B5"
              strokeWidth={2}
              dot={(dotProps: DotProps) => {
                const idx = dotProps.index ?? 0;
                if (isZooming || !isDotTick(idx)) return <circle key={idx} r={0} />;
                return (
                  <circle
                    key={idx}
                    cx={dotProps.cx as number}
                    cy={dotProps.cy as number}
                    r={4}
                    fill="#0DB5B5"
                    stroke="#fff"
                    strokeWidth={2}
                  />
                );
              }}
              activeDot={isZooming ? false : { r: 5, fill: "#0DB5B5", stroke: "#fff", strokeWidth: 2 }}
              isAnimationActive={false}
            >
              {!isZooming && (
                <LabelList
                  dataKey="weight"
                  content={(labelProps) => {
                    const idx = (labelProps.index as number) ?? 0;
                    if (!isDotTick(idx)) return null;
                    return (
                      <text
                        key={idx}
                        x={Number(labelProps.x)}
                        y={Number(labelProps.y) - 10}
                        textAnchor="middle"
                        fontSize={11}
                        fontFamily="Inter, sans-serif"
                        fontWeight={500}
                        fill="#1F1F1F"
                      >
                        {labelProps.value}
                      </text>
                    );
                  }}
                />
              )}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WeightChart;
